import { Injectable, inject } from '@angular/core';
import { PouchDB } from '../db/pouchdb-init';
import { BaseDocumentService } from './base-document.service';
import { DocumentHandler, DOCUMENT_HANDLER } from './document-handler.interface';
import { BaseDocument } from '../types';
import { DataProvider } from '../interfaces';
import { LatLng, LatLngBounds, Point } from 'leaflet';

/**
 * Spezialisierung von BaseDocumentService für räumliche Datenpunkte.
 * Ergänzt BaseDocumentService um:
 * - DocumentHandler-Integration für app-spezifische Transformationen
 * - Räumliche Downloads mit Polygon-Filterung
 * - Views & Filter für komplexe Abfragen
 * - HardData-Verwaltung (Lookup-Tabellen)
 *
 * Subklassen müssen die abstrakten Service-Getter implementieren und
 * einen DocumentHandler via DOCUMENT_HANDLER-Token bereitstellen.
 */
@Injectable()
export abstract class BaseDataService<T extends BaseDocument>
	extends BaseDocumentService<T>
	implements DataProvider<T>
{
	// Injected DocumentHandler für app-spezifische Logik
	protected documentHandler = inject(DOCUMENT_HANDLER) as DocumentHandler<T>;

	// Cache für benutzerdefinierte Listen (app-spezifisch)
	protected customListCache = new Map<string, any[]>();

	// Deduplizierung gleichzeitiger queryIndex()-Aufrufe + kurzlebiger Cache
	private _pendingQueryIndex: Promise<any> | null = null;
	private _queryIndexCache: any = null;
	private _queryIndexCacheTimer: ReturnType<typeof setTimeout> | null = null;

	/** Cache invalidieren (bei Datenänderungen) */
	invalidateQueryCache(): void {
		this._queryIndexCache = null;
		if (this._queryIndexCacheTimer) {
			clearTimeout(this._queryIndexCacheTimer);
			this._queryIndexCacheTimer = null;
		}
	}

	constructor() {
		super();
		// Cache bei Datenänderungen automatisch invalidieren
		this.documentSaved.subscribe(() => this.invalidateQueryCache());
		this.checkedOut.subscribe(() => this.invalidateQueryCache());
		this.checkedIn.subscribe(() => this.invalidateQueryCache());
		this.documentsRefreshRequested.subscribe(() => this.invalidateQueryCache());
	}

	// ========== Aliase für Rückwärtskompatibilität ==========

	// Event-Aliase (pointSaved → documentSaved, etc.)
	get pointSaved() { return this.documentSaved; }
	get pointsDownloadCompleted() { return this.documentsDownloadCompleted; }
	get downloadedPointsDiscarded() { return this.downloadedDocumentsDiscarded; }
	get pointsRefreshRequested() { return this.documentsRefreshRequested; }

	// Property-Aliase (_pointsDownloaded → _documentsDownloaded, etc.)
	protected get _pointsDownloaded() { return this._documentsDownloaded; }
	protected set _pointsDownloaded(value: boolean) { this._documentsDownloaded = value; }
	get lastDownloadedPointsCount() { return this.lastDownloadedDocumentsCount; }
	set lastDownloadedPointsCount(value: number) { this.lastDownloadedDocumentsCount = value; }

	// Methoden-Aliase
	arePointsDownloaded(): boolean {
		return this.areDocumentsDownloaded();
	}

	async arePointsActuallyDownloaded(): Promise<boolean> {
		return this.areDocumentsActuallyDownloaded();
	}

	async arePointsDownloadedForProject(projectId: string): Promise<boolean> {
		return this.areDocumentsDownloadedForProject(projectId);
	}

	async discardDownloadedPoints(): Promise<void> {
		return this.discardLocal();
	}

	// ========== Transformation Hooks (DocumentHandler-Integration) ==========

	/**
	 * Transformiert ein Dokument nach dem Laden aus der DB.
	 * Nutzt den DocumentHandler für app-spezifische Logik.
	 */
	protected override transformAfterLoad(doc: T): T {
		return this.documentHandler.transformAfterLoad(doc);
	}

	/**
	 * Hook: Wird nach erfolgreichem Download aufgerufen.
	 * Erstellt Design-Dokumente für Views/Filter.
	 */
	protected override async afterDownloadComplete(db: PouchDB.Database): Promise<void> {
		await this.ensureApplicationDesignDocument(db);
	}

	// ========== Query Override (nutzt application/index View) ==========

	/**
	 * Query-Methode mit application/index View.
	 * Override von BaseDocumentService (welches allDocs() nutzt).
	 */
	override async queryIndex(include_docs = false): Promise<any> {
		const database = this.db();
		if (!database) {
			console.warn('queryIndex: keine DB verfügbar', { offline: this.oms.offlineMode() });
			return null;
		}
		if (!this.isLiveSync()) {
			const userOffline = this.oms.userOfflineMode();
			const networkOnline = this.oms.networkOnline();
			if (!userOffline && !networkOnline) {
				console.warn('queryIndex: Netzwerk nicht verfügbar, behalte bestehende Punkte');
				return null;
			}
		}

		// Deduplizierung: Bei gleichzeitigen Aufrufen ohne include_docs
		// nur eine Server-Anfrage machen und das Ergebnis teilen.
		// Kurzlebiger Cache (3s) verhindert auch sequentielle Doppelabfragen.
		if (!include_docs) {
			if (this._pendingQueryIndex) return this._pendingQueryIndex;
			if (this._queryIndexCache) return this._queryIndexCache;
		}

		const doQuery = async () => {
			try {
				return await database.query('application/index', { include_docs });
			} catch (err: any) {
				console.error('[queryIndex] Query-Fehler:', err);
				if (err?.status === 404 && err?.name === 'not_found') {
					await this.ensureApplicationDesignDocument(database);
					return await database.query('application/index', { include_docs });
				}
				throw err;
			}
		};

		if (!include_docs) {
			this._pendingQueryIndex = doQuery().then(result => {
				// Ergebnis 3 Sekunden cachen für sequentielle Aufrufe
				this._queryIndexCache = result;
				if (this._queryIndexCacheTimer) clearTimeout(this._queryIndexCacheTimer);
				this._queryIndexCacheTimer = setTimeout(() => { this._queryIndexCache = null; }, 3000);
				return result;
			}).finally(() => {
				this._pendingQueryIndex = null;
			});
			return this._pendingQueryIndex;
		}

		return doQuery();
	}

	// ========== Räumliche Downloads ==========

	/**
	 * Lädt Dokumente für Offline-Nutzung herunter, ohne in den Offline-Modus zu wechseln.
	 * Die App bleibt im Online-Modus und nutzt weiterhin die Remote-Datenbank.
	 * Unterstützt jetzt Polygon-Filterung statt nur Bounding-Box.
	 * Akzeptiert sowohl neue Region-Objekte {type, bounds, polygon?} als auch alte LatLngBounds direkt.
	 */
	public async downloadDocuments(regions?: (any)[], filterName?: string|null): Promise<void> {
		if (this.isLiveSync()) {
			console.warn('[downloadDocuments] Nicht verfügbar im Live-Sync-Modus');
			this.documentsDownloadCompleted.emit();
			return;
		}
		if (!this.ps.project()) return;
		if (!regions || !this.remoteDb) return;

		// Hilfsfunktion: LatLngBounds aus verschiedenen Formaten rekonstruieren
		const reconstructBounds = (b: any): LatLngBounds | null => {
			if (!b || typeof b !== 'object') return null;
			try {
				// Bereits ein LatLngBounds-Objekt mit Methoden
				if (typeof b.getSouthWest === 'function') return b;
				// Serialisiertes Format mit _southWest/_northEast
				if (b._southWest && b._northEast) {
					return new LatLngBounds(
						[b._southWest.lat, b._southWest.lng],
						[b._northEast.lat, b._northEast.lng]
					);
				}
				// Array-Format [[swLat, swLng], [neLat, neLng]]
				if (Array.isArray(b) && b.length === 2) {
					return new LatLngBounds(b[0], b[1]);
				}
				console.warn('[downloadDocuments] Unbekanntes Bounds-Format:', b);
				return null;
			} catch (err) {
				console.error('[downloadDocuments] Fehler beim Rekonstruieren von Bounds:', err, b);
				return null;
			}
		};

		// Regionen zu EPSG:5650 konvertieren (für Bounds und optional Polygone)
		const projectedRegions = regions.map((region: any, i: number) => {
			if (!region || typeof region !== 'object') {
				console.error(`[downloadDocuments] Region[${i}] ist ungültig:`, region);
				return null;
			}

			// Format erkennen: Hat es eine 'bounds' Property (neues Format) oder ist es selbst ein LatLngBounds?
			let bounds: LatLngBounds | null = null;
			let regionType: string = 'bounds';
			let regionPolygon: LatLng[] | undefined;

			if (region.bounds !== undefined) {
				// Neues Region-Format: {type, bounds, polygon?}
				bounds = reconstructBounds(region.bounds);
				regionType = region.type || 'bounds';
				regionPolygon = region.polygon;
			} else if (region._southWest || region._northEast || (typeof region.getSouthWest === 'function')) {
				// Altes Format: Direktes LatLngBounds-Objekt
				bounds = reconstructBounds(region);
				regionType = 'bounds';
			} else {
				console.error(`[downloadDocuments] Region[${i}] hat unbekanntes Format:`, Object.keys(region));
				return null;
			}

			if (!bounds) {
				console.error(`[downloadDocuments] Region[${i}] konnte Bounds nicht rekonstruieren`);
				return null;
			}

			const sw = bounds.getSouthWest();
			const ne = bounds.getNorthEast();

			// Koordinatentransformation: Nutze ts falls vorhanden, sonst keine Transformation
			let minP: Point, maxP: Point;
			if (this.ts && typeof this.ts.toEPSG5650 === 'function') {
				minP = this.ts.toEPSG5650(sw);
				maxP = this.ts.toEPSG5650(ne);
			} else {
				// Fallback: Verwende WGS84-Koordinaten direkt als Point
				minP = new Point(sw.lng, sw.lat);
				maxP = new Point(ne.lng, ne.lat);
			}

			// Polygon-Koordinaten konvertieren falls vorhanden
			let projectedPolygon: {x: number, y: number}[] | undefined;
			if (regionType === 'polygon' && regionPolygon && regionPolygon.length >= 3) {
				projectedPolygon = regionPolygon.map((ll: LatLng) => {
					const latLng = typeof (ll as any).lat === 'number' && typeof (ll as any).lng === 'number'
						? new LatLng((ll as any).lat, (ll as any).lng)
						: ll;
					if (this.ts && typeof this.ts.toEPSG5650 === 'function') {
						return this.ts.toEPSG5650(latLng);
					} else {
						return { x: latLng.lng, y: latLng.lat };
					}
				});
			}

			return {
				type: regionType,
				l: minP,
				u: maxP,
				polygon: projectedPolygon
			};
		}).filter(r => r !== null);

		// Prüfen ob gültige Regionen vorhanden sind
		if (projectedRegions.length === 0) {
			console.error('[downloadDocuments] Keine gültigen Regionen nach Filterung - überspringe Download');
			this.documentsDownloadCompleted.emit();
			return;
		}

		this.isBusy = true;

		// Erstelle lokale DB falls nicht vorhanden, ohne die aktive DB zu wechseln
		if (!this.localDb) {
			try {
				this.localDb = new PouchDB(this.ps.project());
			} catch (err) {
				this.isBusy = false;
				this.documentsDownloadCompleted.emit();
				return;
			}
		}

		try {
			// Clientseitige Filterung: Erst alle Punkt-IDs aus dem View holen, dann filtern
			const viewResult = await this.remoteDb.query('application/index', { include_docs: false });

			// Filter-Funktion: Nutzt DocumentHandler für isDocumentInRegion
			const filteredIds: string[] = [];
			for (const row of viewResult.rows) {
				if (Array.isArray(row.key) && row.key.length >= 4) {
					// View-Schlüssel: [nummer, type, x, y, ...]
					// Dokument rekonstruieren für isDocumentInRegion
					const doc: any = {
						header: {
							koordinaten: {
								x: row.key[2],
								y: row.key[3]
							}
						}
					};

					// Prüfe ob Punkt in einer der Regionen liegt
					for (const r of projectedRegions) {
						if (this.documentHandler.isDocumentInRegion(doc, r)) {
							filteredIds.push(row.id);
							break;
						}
					}
				}
			}

			// Speichere gefilterte Anzahl für Fortschrittsanzeige
			this.lastDownloadedDocumentsCount = filteredIds.length;

			if (filteredIds.length === 0) {
				await this.ensureApplicationDesignDocument(this.localDb);
				this._documentsDownloaded = true;
				await this.storage.setProjectValue('pointsDownloaded', this.ps.project(), true);
				this.documentsDownloadCompleted.emit();
				this.isBusy = false;
				return;
			}

			// Dokumente per ID-basierter Replikation laden
			await this.downloadDocumentsByIds(filteredIds);
		} catch (err) {
			this.loadingProgress = 0;
			console.error('[downloadDocuments] Fehler:', err);
		} finally {
			this.isBusy = false;
		}
	}

	// ========== HardData Methods ==========

	public getHardcoded(table: string, func?: (row: any) => boolean): any[] {
		// Prüfe zuerst ob benutzerdefinierte Liste existiert
		const customList = this.customListCache.get(table);
		const hardDataMap = this.documentHandler.getHardData();
		const tbl = customList ?? hardDataMap.get(table);
		if (!tbl) {
			console.error(`Unknown table ${table}`);
			return [];
		}
		if (!func) {
			return tbl;
		}
		return tbl.filter(func);
	}

	public async loadCustomLists() {
		// Subklassen können dies überschreiben um custom lists zu laden
		// Basis-Implementierung leer, da kein LocalStorageService vorhanden
	}

	public async reloadCustomList(listName: string) {
		// Subklassen können dies überschreiben um custom list neu zu laden
		// Basis-Implementierung leer, da kein LocalStorageService vorhanden
	}

	// ========== View Methods ==========

	protected async ensureApplicationDesignDocument(db: PouchDB.Database, requireConnection = false) {
		// Für lokale DBs ist keine Verbindung nötig (requireConnection=false)
		// Für Remote-DBs beim initialen Setup wird requireConnection=true übergeben
		if (requireConnection && !this.oms.networkOnline()) return false;

		// Design-Dokumente vom DocumentHandler holen
		const designDocs = this.documentHandler.getDesignDocuments();

		// Application Design-Dokument mit Filtern und Views
		const appDesignDoc = designDocs.find(d => d._id === '_design/application');
		if (!appDesignDoc) {
			console.error('DocumentHandler liefert kein _design/application Dokument!');
			return false;
		}

		let existingDoc: any;
		try {
			existingDoc = await db.get<any>('_design/application');
		} catch(err: any) {
			existingDoc = {};
		}

		// Prüfen ob Update nötig ist
		const needsUpdate = !existingDoc
			|| !existingDoc.filters
			|| JSON.stringify(existingDoc.filters) !== JSON.stringify(appDesignDoc.filters)
			|| !existingDoc.views
			|| JSON.stringify(existingDoc.views) !== JSON.stringify(appDesignDoc.views);

		if (needsUpdate) {
			const docToSave = {
				...appDesignDoc,
				_rev: existingDoc._rev
			};
			const result = await db.put(docToSave);
			return !!result?.ok;
		}

		return true;
	}

	storeViews(callback: Function, withBounds: boolean, bnds: {l: {x: number, y: number}, u: {x: number, y: number}}[]) {
		this.storage.get("conditionExpressionObjects").then((filters: any) => {
			const views: any = {};
			for (const [key, value] of Object.entries(filters)) {
				// DocumentHandler verwendet für Filter-Generierung
				// Diese Methode ist sehr app-spezifisch, daher hier nur Basis-Struktur
				views[key] = {
					"map": `function(doc) { emit(doc._id); }`,
					"object": value
				};
			}
		this.db()?.get<any>('_design/filter')
			.then( (filterDoc: any) => {
				filterDoc.views = views;
				this.db()?.put(filterDoc).then(() => {
					callback();
				}).catch((err: any) => {
					console.error("Error saving filter functions:", err);
				});
			})
			.catch((err: Error) => {
				this.db()?.put({
					"_id": "_design/filter",
					"views": views,
				}).then(() => { callback(); });
			});
		});
	}

	async getView(name: string) {
		return this.db()?.query(`filter/${name}`);
	}

	getFilter() {
		return this.db()?.get("_design/filter");
	}
}
